import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { branchesApi } from '@/api/branches.api';
import type { Branch } from '@/types/branch.types';

interface BranchContextType {
  branches: Branch[];
  selectedBranch: Branch | null;
  isAllBranches: boolean;
  selectBranch: (branchId: string | 'all') => void;
  refreshBranches: () => Promise<void>;
  isLoading: boolean;
  canSelectAllBranches: boolean;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

interface BranchProviderProps {
  children: ReactNode;
}

export const BranchProvider = ({ children }: BranchProviderProps) => {
  const { clinic, user } = useAuth();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [isAllBranches, setIsAllBranches] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const refreshBranches = useCallback(async () => {
    if (!clinic?.id) return;
    try {
      setIsLoading(true);
      // Archived branches stay listed in Settings (to be restored) but are
      // never a working context: they take no bookings.
      let data = (await branchesApi.getAll()).filter((b) => !b.isArchived);

      // Filter branches based on user's access permissions. A user limited
      // to selected branches with none listed sees none, not all of them.
      if (user && user.accessAllBranches === false) {
        const accessibleBranchIds = (user.branches ?? []).map(
          (b) => b.branchId,
        );
        data = data.filter((b) => accessibleBranchIds.includes(b.id));
      }

      setBranches(data);

      const storedBranchId = localStorage.getItem(
        `selectedBranchId_${clinic.id}`,
      );

      // If user doesn't have access to all branches, don't allow "all" selection
      const canSelectAll = !user || user.accessAllBranches;

      if (storedBranchId === 'all' && canSelectAll) {
        setSelectedBranch(null);
        setIsAllBranches(true);
        return;
      }

      const restoredBranch = storedBranchId
        ? data.find((b) => b.id === storedBranchId)
        : null;
      const fallbackBranch = restoredBranch ?? data[0] ?? null;

      if (fallbackBranch) {
        setSelectedBranch(fallbackBranch);
        setIsAllBranches(false);
        localStorage.setItem(
          `selectedBranchId_${clinic.id}`,
          fallbackBranch.id,
        );
      } else {
        setSelectedBranch(null);
        setIsAllBranches(true);
      }
    } catch (err) {
      console.error('Failed to load branches:', err);
    } finally {
      setIsLoading(false);
    }
  }, [clinic?.id, user]);

  useEffect(() => {
    if (clinic?.id) {
      refreshBranches();
    }
  }, [clinic?.id, refreshBranches]);

  const canSelectAllBranches = !user || user.accessAllBranches !== false;

  const selectBranch = (branchId: string | 'all') => {
    if (branchId === 'all' && canSelectAllBranches) {
      setSelectedBranch(null);
      setIsAllBranches(true);
      if (clinic?.id) {
        localStorage.setItem(`selectedBranchId_${clinic.id}`, 'all');
      }
    } else if (branchId !== 'all') {
      const branch = branches.find((b) => b.id === branchId);
      if (branch) {
        setSelectedBranch(branch);
        setIsAllBranches(false);
        if (clinic?.id) {
          localStorage.setItem(`selectedBranchId_${clinic.id}`, branchId);
        }
      }
    }
  };

  return (
    <BranchContext.Provider
      value={{
        branches,
        selectedBranch,
        isAllBranches,
        selectBranch,
        refreshBranches,
        isLoading,
        canSelectAllBranches,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
};
